import React, { FC, useMemo } from 'react';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Box, GreenButton, WhiteButton } from '~Styles';
import EditIcon from '~Assets/icons/edit.svg';
import { DateTime } from 'luxon';
import styled from '@emotion/styled';
import { Cheque } from '@baemingo/table-events/dist/types';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Modal, { insideWrapperStyles } from '~ComponentsImproved/Modal';
import MessageInput from '~ComponentsImproved/MessageInput';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useI18n, useLicenses, usePrintBill, useSelector, useStaffCan } from '~Hooks';
import useOrderParking from '~Hooks/useOrderParking';
import Bill from '~Scenes/Tables/Bill';
import ServiceTypeChanger from '~Scenes/POS/Components/Cart/Components/ServiceTypeChanger';
import { SERVICE_TYPE } from '@baemingo/order-status-events/dist/types';
import MoveToTable from './MoveToTable';
import DeleteIcon from '~/Assets/icons/delete.svg';
import { parseParkedOrderName } from '~Scenes/ParkedOrders/utils';
import { useTableUniverse } from '~Components/TableUniverse';
import useBongQuery from '~Scenes/POS/Queries/query';
import useCategories from '~Scenes/POS/categories';
import Articles from '~Scenes/Tables/Components/Articles';
import RemoveItems from '~Components/RemoveItems';
import ReceiptIcon from '~/Assets/icons/receipt_long.svg';
import useConfirm from '~Hooks/useConfirm';
import TimeIcon from '~Assets/icons/schedule.svg';
import PersonIcon from '~Assets/icons/person.svg';
import { GET_USER } from '~Components/LinkUser/api';
import { useQuery } from '@apollo/client';
import { GetUser, GetUserVariables } from '~Components/LinkUser/__queries__';
import MembershipLevelBadge from '~Components/LinkUser/SelectedUser/MembershipLevelBadge';

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  width: 440px;
  max-height: calc(100vh - 168px);
  margin-bottom: 20px; // this because we ignore padding bottom in the parent to enable scroll to bottom for the left section
  border: 1px solid var(--colorBlueGray700);
`;

const ModalWrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};

  height: 90vh;
  width: 90vw;
`;

const DeletedItemsModalWrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
`;

const DeletedItemsWrapper = styled.div`
  overflow: auto;
  padding-bottom: 16px;
  max-height: 500px;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  color: var(--colorWhite);

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DeletedItemsButton = styled.div`
  button {
    min-width: 100px;
  }
`;

const HeaderAndItemsWrapper = styled(Box)`
  height: 100%;
  overflow: hidden;
`;

/** Top of the parked order - shows name (with action button to edit name) & time */
const TopBar = styled(Box)`
  border-bottom: 1px solid var(--colorBlueGray700);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 16px;
`;

/** Bottom bar with action buttons */
const BottomBar = styled(Box)`
  padding: 0 16px 16px 16px;
`;

const DeleteIconWrapper = styled.div`
  height: 32px;
  width: 32px;
  mask-image: url(${DeleteIcon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: var(--colorBlueGray200);
`;

const BillIconWrapper = styled.div`
  height: 32px;
  width: 32px;
  mask-image: url(${ReceiptIcon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: var(--colorBlueGray200);
`;

interface Props {
  selectedParkedOrder: Cheque;
  onSelectedParkedOrderChange: (order: Cheque | null) => void;
  selectFirstParkedOrder: (removedId?: string) => void;
}

/**
 * Detailed description of the parked order that is selected with action buttons to:
 * - Edit the name of the parked order
 * - Move the parked order to a table
 * - Proceed to payment with the parked order
 */
const ParkedOrderDetails: FC<Props> = ({
  selectedParkedOrder,
  onSelectedParkedOrderChange,
  selectFirstParkedOrder,
}) => {
  const { i18n } = useI18n();
  const universe = useTableUniverse();
  const licenses = useLicenses();
  const { confirm } = useConfirm();
  const printBill = usePrintBill({
    selectedCheque: selectedParkedOrder.id,
    /** The following will result in Table: -- Guests: -- in the bill */
    selectedTable: { id: '', name: '' },
  });

  const country = useSelector((state) => state.country);
  const venueId = useSelector((state) => state.venueId);
  const companyStaff = useSelector((state) => state.companyStaff);
  const staffId = useSelector((state) => state.staff?.id);

  const bongQuery = useBongQuery(venueId);
  const categories = useCategories(bongQuery.data, []);

  const loading = categories.allItems.length === 0;
  const cheque = universe.cheques.find((c) => c.id === selectedParkedOrder.id);

  const staffWhoParkedOrder = companyStaff?.staffs.find(
    (staff) => staff.id === selectedParkedOrder.owner,
  );

  const orderParking = useOrderParking();
  const staffCan = useStaffCan();

  const editNameModal = useModal();
  const deleteModal = useModal();
  const moveToTableModal = useModal();
  const deletedItemsModal = useModal();

  /** If cheque contains this userId we've just fetched it and it should be available in cache */
  const userQuery = useQuery<GetUser, GetUserVariables>(GET_USER, {
    variables: { id: cheque?.userId || '' },
    skip: !cheque?.userId,
  });
  const selectedUser = userQuery.data?.User;
  // Fetch membership level from different places if the company uses membershipCards or not.
  const userMembership =
    selectedUser?.membershipCard?.membershipLevel ?? selectedUser?.membershipLevel;

  // Deleted items
  const deletedItems = useMemo(() => {
    if (categories.allItems.length === 0) {
      return [];
    }

    const chequeItems = universe.items.filter((c) => c.chequeId === selectedParkedOrder.id);
    const items = [];

    for (const chequeItem of chequeItems) {
      const item = categories.allItems.find((c) => c.id === chequeItem.itemId);
      if (!item) {
        items.push(chequeItem);
      }
    }

    return items;
  }, [categories.allItems, selectedParkedOrder.id]);

  const moveToTableButton = useButton({
    onClick: () => {
      moveToTableModal.toggle();
    },
  });

  const toPaymentButton = useButton({
    onClick: () => {
      /**
       * Moving an order that contains a deleted article causes a crash.
       * To prevent this, we display a warning and do not add the deleted items to the cart
       */
      if (deletedItems.length > 0) {
        deletedItemsModal.toggle();
        return;
      }

      orderParking.toPayment(selectedParkedOrder.id, userMembership?.discountPercentage);
    },
    disabled: loading,
  });

  const confirmToPaymentButton = useButton({
    onClick: () => {
      orderParking.toPayment(selectedParkedOrder.id, userMembership?.discountPercentage);
    },
  });

  const cancelToPaymentButton = useButton({
    onClick: () => {
      deletedItemsModal.toggle();
    },
  });

  /** Edits the parked order name - meaning, we're editing the cheques name */
  const handleEditName = (id: string, name: string) => {
    const editedName = orderParking.editName(id, name);

    if (selectedParkedOrder) {
      onSelectedParkedOrderChange({ ...selectedParkedOrder, name: editedName });
    }

    editNameModal.hide();
  };

  const handlePrintBill = async () => {
    const isConfirmed = await confirm(i18n('ParkedOrder.PrintBill'));

    if (isConfirmed) {
      printBill.printBill();
    }
  };

  const parsedOrder = parseParkedOrderName(selectedParkedOrder.name ?? '');

  /** Use the same permission logic as for a table in TSR */
  const staffCanDeleteParkedOrder =
    staffCan.REFUND && staffCan.mutateCheque(selectedParkedOrder?.id ?? '');

  const orderNameToDisplay = parsedOrder.orderName || i18n('ParkedOrders.Untitled');

  return (
    <>
      <Wrapper display="flex" flexDirection="column" justifyContent="space-between">
        <HeaderAndItemsWrapper display="flex" flexDirection="column">
          <TopBar display="flex" flexDirection="column" gap="xs">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {parsedOrder.orderEasyId && (
                <Paragraph level={4} color="var(--colorBlueGray200)">
                  {parsedOrder.orderEasyId.includes('•')
                    ? parsedOrder.orderEasyId
                    : `#${parsedOrder.orderEasyId}`}
                </Paragraph>
              )}
              <Paragraph level={5} color="var(--colorBlueGray100)">
                {selectedParkedOrder.createdAt
                  ? DateTime.fromISO(selectedParkedOrder.createdAt).toFormat('dd LLL HH:mm')
                  : ''}
              </Paragraph>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap="xs" onClick={editNameModal.toggle}>
                <Heading
                  level={
                    orderNameToDisplay?.length > 20 ? 7 : orderNameToDisplay?.length > 15 ? 6 : 4
                  }
                  color={parsedOrder.orderName ? 'var(--colorWhite)' : 'var(--colorBlueGray200)'}>
                  {orderNameToDisplay}
                </Heading>
                <Icon src={EditIcon} color="var(--colorBlueGray600)" />
              </Box>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Paragraph level={5} color="var(--colorBlueGray200)">
                {i18n('ParkedOrders.Device')}: {parsedOrder.deviceName}
              </Paragraph>
              {staffWhoParkedOrder && (
                <Paragraph level={5} color="var(--colorBlueGray200)">
                  {i18n('ParkedOrders.Staff')}: {staffWhoParkedOrder?.name}
                </Paragraph>
              )}
            </Box>

            {parsedOrder.desiredTime && (
              <Box display="flex" alignItems="center" gap="xs">
                <Icon src={TimeIcon} color={'var(--colorBlueGray700)'} />
                <Paragraph level={4} color="var(--colorWhite)">
                  {i18n('DesiredTime.Title')}:{' '}
                  {DateTime.fromISO(parsedOrder.desiredTime).toFormat('HH:mm dd/MM')}
                </Paragraph>
              </Box>
            )}

            {cheque?.userId && (
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap="xs">
                  <Icon src={PersonIcon} color={'var(--colorBlueGray700)'} />
                  <Paragraph level={4} color="var(--colorWhite)">
                    {selectedUser?.name}
                  </Paragraph>
                </Box>
                {userMembership && (
                  <MembershipLevelBadge
                    title={i18n(userMembership?.title)}
                    primaryColor={userMembership?.primaryColor || 'var(--colorBlueGray200)'}
                  />
                )}
              </Box>
            )}
          </TopBar>

          <ServiceTypeChanger
            parkedOrderServiceType={
              (parsedOrder.serviceType as SERVICE_TYPE) ?? ('EAT_HERE' as SERVICE_TYPE)
            }
          />

          <Bill
            isParkedOrder
            tableId="Parked"
            tableName={selectedParkedOrder.name ?? i18n('ParkedOrders.Untitled')}
            selectedCheque={selectedParkedOrder.id}
          />
        </HeaderAndItemsWrapper>

        <BottomBar
          display="flex"
          alignItems="center"
          justifyContent={
            staffCanDeleteParkedOrder || country === 'ES' ? 'space-between' : 'flex-end'
          }>
          <Box display="flex" alignItems="center" gap="xs">
            {staffCanDeleteParkedOrder && <DeleteIconWrapper onTouchStart={deleteModal.toggle} />}
            {country === 'ES' && <BillIconWrapper onTouchStart={handlePrintBill} />}
          </Box>

          <Box display="flex" alignItems="center" gap="small">
            {licenses.atleastOneDeviceHasTSR && (
              <WhiteButton size="small" {...moveToTableButton}>
                {i18n('ParkedOrders.MoveToTable')}
              </WhiteButton>
            )}
            <GreenButton size="small" {...toPaymentButton}>
              {i18n('ParkedOrders.Proceed')}
            </GreenButton>
          </Box>
        </BottomBar>
      </Wrapper>

      {/* Modals */}
      <Modal isShowing={editNameModal.isShowing} hide={editNameModal.hide}>
        <MessageInput
          type="text"
          value={parsedOrder.orderName ?? ''}
          autoFocus
          placeHolder={i18n('ParkedOrders.Name.Placeholder')}
          onSubmit={(name) => handleEditName(selectedParkedOrder?.id ?? '', name)}
        />
      </Modal>

      <Modal isShowing={deleteModal.isShowing} hide={deleteModal.toggle}>
        <RemoveItems
          onComplete={(message, commentOnWaste) => {
            orderParking.remove(selectedParkedOrder.id, message, undefined, commentOnWaste);
            deleteModal.hide();
          }}
        />
      </Modal>

      <Modal isShowing={moveToTableModal.isShowing} hide={moveToTableModal.toggle}>
        <ModalWrapper>
          <MoveToTable
            parkedOrderId={selectedParkedOrder.id}
            toggleModal={moveToTableModal.toggle}
          />
        </ModalWrapper>
      </Modal>

      <Modal
        isShowing={deletedItemsModal.isShowing}
        hide={deletedItemsModal.toggle}
        position={'center'}>
        <DeletedItemsModalWrapper display="flex" flexDirection="column" gap="xs" padding="medium">
          <Box display="flex" flexDirection="column" alignItems="center" padding="small" gap="xs">
            <Heading level={2} color="var(--colorWhite)" bottom={2}>
              {i18n('ParkedOrders.DeletedItems.Title')}
            </Heading>
            <Paragraph level={4} color="var(--colorBlueGray200)">
              {i18n('ParkedOrders.DeletedItems.Desc')}
            </Paragraph>
          </Box>

          <DeletedItemsWrapper>
            <Articles
              items={deletedItems}
              staffId={staffId}
              selectedItems={[]}
              setSelectedItems={() => {}}
            />
          </DeletedItemsWrapper>

          <Box display="flex" alignItems="center" justifyContent="center" marginBottom={'small'}>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('ParkedOrders.DeletedItems.Description')}
            </Paragraph>
          </Box>

          <Box display="flex" alignItems="center" gap="small" justifyContent="flex-end">
            <DeletedItemsButton>
              <WhiteButton size="medium" {...cancelToPaymentButton}>
                {i18n('General.No')}
              </WhiteButton>
            </DeletedItemsButton>
            <DeletedItemsButton>
              <GreenButton size="medium" {...confirmToPaymentButton}>
                {i18n('General.Yes')}
              </GreenButton>{' '}
            </DeletedItemsButton>
          </Box>
        </DeletedItemsModalWrapper>
      </Modal>
    </>
  );
};

export default ParkedOrderDetails;
